import React, {useRef, useEffect, useState} from 'react';
import {
  Modal,
  TouchableWithoutFeedback,
  Platform,
  Keyboard,
  Animated,
  PanResponder,
  Dimensions,
  View,
} from 'react-native';
import {
  ModalContainer,
  ModalContent,
  ModalMask,
  Handle,
  ModalContentContainer,
  SlideshowBackground,
} from './styles';

interface Props {
  isOpen: boolean;
  children: React.ReactNode;
  onClose: () => void;
  dismissable?: boolean;
  showHandle?: boolean;
}

const DURATION = 300;
const SCREEN_HEIGHT = Dimensions.get('window').height;

export default function LegacyBottomSheet({
  isOpen,
  children,
  onClose,
  dismissable = true,
}: Props) {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(SCREEN_HEIGHT)).current;
  const [modalVisible, setModalVisible] = useState(false);
  const isOpenRef = useRef(isOpen);
  const panY = useRef(new Animated.Value(0)).current;

  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, {dy, dx}) => dy > 8 && dy > Math.abs(dx),
      onPanResponderMove: (_, {dy}) => {
        if (dy > 0) { panY.setValue(dy); }
      },
      onPanResponderRelease: (_, {dy, vy}) => {
        if (dy > 100 || vy > 0.5) {
          onClose();
        } else {
          Animated.spring(panY, {toValue: 0, useNativeDriver: true}).start();
        }
      },
      onPanResponderTerminate: () => {
        Animated.spring(panY, {toValue: 0, useNativeDriver: true}).start();
      },
    }),
  ).current;

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      setModalVisible(true);
      Animated.parallel([
        Animated.timing(fadeAnim, {toValue: 1, duration: DURATION, useNativeDriver: true}),
        Animated.timing(slideAnim, {toValue: 0, duration: DURATION, useNativeDriver: true}),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {toValue: 0, duration: DURATION, useNativeDriver: true}),
        Animated.timing(slideAnim, {toValue: SCREEN_HEIGHT, duration: DURATION, useNativeDriver: true}),
      ]).start(({finished}) => {
        if (finished && !isOpenRef.current) { setModalVisible(false); panY.setValue(0); }
      });
    }
  }, [isOpen]);

  return (
    <Modal
      transparent
      animationType="none"
      visible={modalVisible}
      onRequestClose={dismissable ? onClose : undefined}>

      <SlideshowBackground style={{opacity: fadeAnim}}>
        <ModalMask />
      </SlideshowBackground>

      <ModalContainer>
        <ModalContentContainer
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}>

          <TouchableWithoutFeedback
            onPress={dismissable ? onClose : undefined}
            accessible={false}>
            <View style={{flex: 1}} />
          </TouchableWithoutFeedback>

          <Animated.View
            style={{transform: [{translateY: Animated.add(slideAnim, panY)}]}}
            {...panResponder.panHandlers}>
            <TouchableWithoutFeedback
              onPress={() => Platform.OS !== 'web' && Keyboard.dismiss()}
              accessible={false}>
              <ModalContent>
                <Handle />
                {children}
              </ModalContent>
            </TouchableWithoutFeedback>
          </Animated.View>
        </ModalContentContainer>
      </ModalContainer>
    </Modal>
  );
}
